import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faHeadset, faMessages} from '@fortawesome/pro-light-svg-icons';
import {useTranslation} from 'react-i18next';
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@src/components/shadcn/sidebar';
import {Link} from 'react-router-dom';
import CollapsibleLink from './CollapsibleLink';
import {NavItem} from './types';

const MenuLink = ({item}: {item: NavItem}) => {
  const Tag = item.isLinkToActiveApp ? Link : 'a';
  return (
    <SidebarMenuItem key={item.label}>
      <SidebarMenuButton isActive={item.isActive} asChild>
        <Tag
          className="flex gap-2"
          to={item.to}
          href={!item.isLinkToActiveApp ? item.to : undefined}
          target={item.openInNewTab ? '_blank' : undefined}
          rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
        >
          {item.icon && (
            <FontAwesomeIcon
              icon={item.icon}
              className="text-peppercorn-800 w-5 h-5 transition-transform"
            />
          )}
          <span>{item.label}</span>
        </Tag>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const MenuSection = ({
  sectionName,
  items,
  liveChatEnabled,
  openLiveChat,
  handleSupportLabelClicked,
}: {
  sectionName: string;
  items: NavItem[];
  liveChatEnabled: boolean;
  openLiveChat?: (() => void) | null;
  handleSupportLabelClicked: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const {t} = useTranslation();
  if (!items || items.length === 0) {
    return null;
  }
  return (
    <>
      <SidebarGroupLabel>{sectionName}</SidebarGroupLabel>
      <SidebarGroupContent className="pl-2 pr-1">
        {items.map((item: NavItem) =>
          item.subNavItems && item.subNavItems.length > 0 ? (
            <CollapsibleLink
              key={item.label}
              item={{...item, subNavItems: item.subNavItems || []}}
            />
          ) : (
            <MenuLink key={item.label} item={item} />
          )
        )}
        {sectionName === t('components.Navigation.sections.supportResources') && (
          <>
            {liveChatEnabled && openLiveChat && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild onClick={openLiveChat}>
                  <button
                    type="button"
                    className="bg-transparent border-none flex gap-2 cursor-pointer"
                  >
                    <FontAwesomeIcon
                      icon={faMessages}
                      className="text-peppercorn-800 w-5 h-5 transition-transform"
                    />

                    <span>{t('components.Navigation.SupportResources.liveChat')}</span>
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            <SidebarMenuItem>
              <SidebarMenuButton asChild onClick={handleSupportLabelClicked}>
                <button
                  type="button"
                  className="bg-transparent border-none flex gap-2 cursor-pointer"
                >
                  <FontAwesomeIcon
                    icon={faHeadset}
                    className="text-peppercorn-800 w-5 h-5 transition-transform"
                  />

                  <span>{t('components.Navigation.SupportResources.support')}</span>
                </button>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </>
        )}
      </SidebarGroupContent>
    </>
  );
};

export default function NavMain({
  sections,
  liveChatEnabled,
  openLiveChat,
  handleSupportLabelClicked,
}: {
  sections: Record<string, NavItem[]>;
  liveChatEnabled: boolean;
  openLiveChat?: (() => void) | null;
  handleSupportLabelClicked: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const sectionComponents = React.useMemo(
    () =>
      Object.entries(sections).map(([sectionName, links]) => (
        <MenuSection
          key={sectionName}
          sectionName={sectionName}
          items={links}
          liveChatEnabled={liveChatEnabled}
          openLiveChat={openLiveChat}
          handleSupportLabelClicked={handleSupportLabelClicked}
        />
      )),
    [sections, liveChatEnabled, openLiveChat, handleSupportLabelClicked]
  );
  return (
    <SidebarContent>
      <SidebarGroup>
        <SidebarMenu className="pl-2 pr-1">{sectionComponents}</SidebarMenu>
      </SidebarGroup>
    </SidebarContent>
  );
}
